import { alpha, decomposeColor, recomposeColor, styled } from "@mui/material/styles";

import {
  FILTER_EDITOR_HEIGHT,
  REMOVED_ROW_CLASS,
  ROW_HEIGHT,
  TREE_TOGGLE_WIDTH,
} from "./constants";

function blend(foreground, background) {
  const front = decomposeColor(foreground);
  const back = decomposeColor(background);
  const opacity = front.values[3] ?? 1;

  return recomposeColor({
    type: "rgb",
    values: [0, 1, 2].map(index =>
      Math.round(front.values[index] * opacity + back.values[index] * (1 - opacity))
    ),
  });
}

function toRgb(color) {
  const { type, values } = decomposeColor(color);

  if (type === "rgb" || type === "rgba") return color;

  return recomposeColor({ type: "rgb", values: values.slice(0, 3) });
}

export function gridColors(theme) {
  const paper = toRgb(theme.palette.background.paper);
  const neutral = blend(theme.palette.background.neutral || theme.palette.grey[200], paper);
  const isDark = theme.palette.mode === "dark";

  return {
    paper,
    header: paper,
    alternate: blend(alpha(theme.palette.grey[500], isDark ? 0.08 : 0.04), paper),
    hover: blend(alpha(theme.palette.grey[500], isDark ? 0.16 : 0.08), paper),
    selected: blend(alpha(theme.palette.primary.main, isDark ? 0.07 : 0.04), paper),
    selectedHover: blend(alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08), paper),
    dropInside: blend(alpha(theme.palette.primary.main, isDark ? 0.24 : 0.12), paper),
    group: neutral,
    border: theme.palette.divider,
    removed: isDark ? theme.palette.error.main : theme.palette.error.dark,
  };
}

export const GridRoot = styled("div")(({ theme }) => {
  const colors = gridColors(theme);

  return {
    "--cg-paper": colors.paper,
    "--cg-header": colors.header,
    "--cg-alternate": colors.alternate,
    "--cg-hover": colors.hover,
    "--cg-selected": colors.selected,
    "--cg-selected-hover": colors.selectedHover,
    "--cg-group": colors.group,
    "--cg-border": colors.border,
    "--cg-row-height": `${ROW_HEIGHT}px`,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0,
    backgroundColor: colors.paper,
    color: theme.palette.text.primary,
    ...theme.typography.body2,
    "&.cg-bordered": {
      border: `1px solid ${colors.border}`,
      borderRadius: theme.shape.borderRadius,
      overflow: "hidden",
    },

    "& .cg-scroll": {
      position: "relative",
      flex: "1 1 auto",
      minHeight: 0,
      overflow: "auto",
      scrollbarGutter: "stable",
      scrollbarWidth: "thin",
      scrollbarColor: `${alpha(theme.palette.grey[500], 0.48)} transparent`,
      "&::-webkit-scrollbar": { width: 10, height: 10 },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: alpha(theme.palette.grey[500], 0.48),
        borderRadius: 8,
        border: `2px solid ${colors.paper}`,
      },
      "&::-webkit-scrollbar-corner": { backgroundColor: "transparent" },
    },

    "& table.cg-table": {
      borderCollapse: "separate",
      borderSpacing: 0,
      tableLayout: "fixed",
      minWidth: "100%",
    },
    "& table.cg-table.cg-measuring .cg-filter-row th > *": {
      position: "absolute",
    },
    "& table.cg-table.cg-measuring": {
      tableLayout: "auto",
      width: "auto",
      minWidth: 0,
      "& th, & td": { maxWidth: "none" },
    },

    "& .cg-table th, & .cg-table td": {
      boxSizing: "border-box",
      height: ROW_HEIGHT,
      padding: theme.spacing(0, 1.25),
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      verticalAlign: "middle",
      borderBottom: `1px solid ${colors.border}`,
      backgroundColor: colors.paper,
      textAlign: "left",
    },
    "&.cg-column-lines .cg-table th, &.cg-column-lines .cg-table td": {
      borderRight: `1px solid ${colors.border}`,
    },
    "&:not(.cg-row-lines) .cg-table tbody td": {
      borderBottomColor: "transparent",
    },
    "& .cg-table [data-align='right']": { textAlign: "right" },
    "& .cg-table [data-align='center']": { textAlign: "center" },

    "& .cg-table thead th": {
      position: "sticky",
      top: 0,
      zIndex: 3,
      backgroundColor: colors.header,
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightSemiBold ?? 600,
      fontSize: theme.typography.pxToRem(13),
      userSelect: "none",
    },
    "& .cg-table thead tr.cg-filter-row th": {
      top: `var(--cg-header-height, ${ROW_HEIGHT}px)`,
      backgroundColor: colors.paper,
      padding: theme.spacing(0, 0.5),
      fontWeight: theme.typography.fontWeightRegular,
    },
    "& .cg-table thead th.cg-fixed": { zIndex: 4 },
    "& .cg-table .cg-fixed": { position: "sticky", zIndex: 1 },
    "& .cg-table .cg-fixed-left-edge": {
      boxShadow: `inset -1px 0 0 ${colors.border}, 4px 0 6px -4px ${alpha(
        theme.palette.common.black,
        0.18
      )}`,
    },
    "& .cg-table .cg-fixed-right-edge": {
      boxShadow: `inset 1px 0 0 ${colors.border}, -4px 0 6px -4px ${alpha(
        theme.palette.common.black,
        0.18
      )}`,
    },

    "&.cg-row-alternation .cg-table tbody tr.cg-data-row.cg-alt > td": {
      backgroundColor: colors.alternate,
    },
    "&.cg-hover .cg-table tbody tr.cg-data-row:hover > td": {
      backgroundColor: colors.hover,
    },
    "& .cg-table tbody tr.cg-data-row.cg-selected > td, &.cg-row-alternation .cg-table tbody tr.cg-data-row.cg-alt.cg-selected > td":
      {
        backgroundColor: colors.selected,
      },
    "&.cg-hover .cg-table tbody tr.cg-data-row.cg-selected:hover > td": {
      backgroundColor: colors.selectedHover,
    },
    "& .cg-table tbody tr.cg-clickable": { cursor: "pointer" },

    [`& .cg-table tbody tr.${REMOVED_ROW_CLASS} > td`]: {
      color: `${colors.removed} !important`,
    },

    "& .cg-table tbody tr.cg-group-row > td": {
      backgroundColor: colors.group,
      fontWeight: theme.typography.fontWeightSemiBold ?? 600,
    },
    "& .cg-table td.cg-group-cell": {
      padding: 0,
      overflow: "visible",
    },
    "& .cg-group-content": {
      position: "sticky",
      left: "var(--cg-group-left, 0px)",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      width: "var(--cg-group-width, 100%)",
      maxWidth: "var(--cg-group-width, 100%)",
      minWidth: 0,
      height: ROW_HEIGHT - 1,
      paddingRight: theme.spacing(1.5),
      overflow: "hidden",
      whiteSpace: "nowrap",
    },

    "&.cg-bordered.consuman-tree-list": {
      borderRadius: theme.shape.borderRadius * 2,
    },
    "& .cg-table td.cg-tree-column": {
      textOverflow: "clip",
    },
    "& .cg-tree-cell": {
      display: "flex",
      alignItems: "center",
      minWidth: 0,
    },
    "& .cg-tree-toggle": {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flex: `0 0 ${TREE_TOGGLE_WIDTH}px`,
      width: TREE_TOGGLE_WIDTH,
      height: TREE_TOGGLE_WIDTH,
      borderRadius: "50%",
      color: theme.palette.text.secondary,
    },
    "& .cg-tree-toggle-active": {
      cursor: "pointer",
      transition: theme.transitions.create(["background-color", "color"], {
        duration: theme.transitions.duration.shorter,
      }),
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
    },
    "& .cg-tree-select": {
      flexShrink: 0,
    },
    "& .cg-tree-value": {
      display: "flex",
      alignItems: "center",
      flex: "1 1 auto",
      minWidth: 0,
      overflow: "hidden",
      "& > *": { minWidth: 0, maxWidth: "100%" },
    },
    "& .cg-tree-text": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .cg-header-prefix": {
      display: "inline-flex",
      alignItems: "center",
      flexShrink: 0,
      marginLeft: TREE_TOGGLE_WIDTH,
    },
    "& .cg-table tbody tr.cg-row-dragging > td": {
      opacity: 0.45,
    },
    "&.cg-dragging-active .cg-table tbody tr.cg-data-row.cg-tree-row.cg-drop-inside > td": {
      backgroundColor: colors.dropInside,
      boxShadow: `inset 0 1px 0 ${theme.palette.primary.main}, inset 0 -1px 0 ${theme.palette.primary.main}`,
    },
    "&.cg-dragging-active, &.cg-dragging-active *": {
      cursor: "grabbing !important",
      userSelect: "none",
    },
    "&.cg-dragging-active.cg-drop-invalid, &.cg-dragging-active.cg-drop-invalid *": {
      cursor: "no-drop !important",
    },

    "& .cg-table tbody .MuiSwitch-root": {
      marginTop: -2,
      marginBottom: -2,
    },
    "& .cg-table td.cg-command, & .cg-table th.cg-command": {
      padding: 0,
      textAlign: "center",
      textOverflow: "clip",
    },
    "& .cg-table td.cg-indent": {
      padding: 0,
      borderRight: "none",
    },

    "& .cg-table thead tr.cg-header-row th:not(.cg-command)": {
      padding: 0,
    },
    "& .cg-header-cell": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.25),
      minWidth: 0,
      height: "100%",
      padding: theme.spacing(0, 1.25),
      outline: "none",
      "&:focus-visible": {
        boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.48)}`,
      },
    },
    "& .cg-header-cell[data-align='right']": { flexDirection: "row-reverse" },
    "& .cg-header-caption": {
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .cg-header-cell[data-align='center'] .cg-header-caption": {
      flex: "1 1 auto",
      textAlign: "center",
    },
    "& th.cg-sortable": { cursor: "pointer" },
    "& th .cg-header-actions": {
      position: "absolute",
      top: "50%",
      right: 8,
      transform: "translateY(-50%)",
      display: "flex",
      alignItems: "center",
      gap: 2,
      paddingLeft: 12,
      background: `linear-gradient(to right, transparent, ${colors.header} 10px)`,
      opacity: 0,
      pointerEvents: "none",
      transition: theme.transitions.create("opacity", {
        duration: theme.transitions.duration.shorter,
      }),
    },
    "& th:hover .cg-header-actions, & th .cg-header-actions:focus-within": {
      opacity: 1,
      pointerEvents: "auto",
    },
    "& th .cg-sort-icon": { flexShrink: 0, color: theme.palette.text.primary },
    "& th.cg-drop-before": { boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}` },
    "& th.cg-drop-after": { boxShadow: `inset -3px 0 0 ${theme.palette.primary.main}` },
    "& th.cg-dragging": { opacity: 0.5 },

    "& .cg-resize-handle": {
      position: "absolute",
      top: 0,
      right: 0,
      width: 7,
      height: "100%",
      cursor: "col-resize",
      zIndex: 2,
      touchAction: "none",
      "&:hover, &.cg-resizing": {
        backgroundColor: alpha(theme.palette.primary.main, 0.32),
      },
    },

    "& .cg-filter-editor": {
      display: "flex",
      alignItems: "center",
      height: FILTER_EDITOR_HEIGHT,
      padding: theme.spacing(0, 0.25),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: alpha(theme.palette.grey[500], 0.08),
      border: "1px solid transparent",
      transition: theme.transitions.create(["background-color", "border-color"]),
      "&:hover": { backgroundColor: alpha(theme.palette.grey[500], 0.12) },
      "&.Mui-focused, &:focus-within": {
        backgroundColor: colors.paper,
        borderColor: theme.palette.primary.main,
      },
      "&.cg-active": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
      "& input": {
        ...theme.typography.body2,
        padding: theme.spacing(0, 0.5),
        height: FILTER_EDITOR_HEIGHT - 2,
      },
    },

    "& .cg-empty": {
      position: "sticky",
      left: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(1),
      height: ROW_HEIGHT * 3,
      color: theme.palette.text.disabled,
    },

    "& .cg-loading": {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 5,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    },

    "& .cg-loading-panel": {
      display: "flex",
      padding: 13,
      borderRadius: "50%",
      border: `1px solid ${
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.black, 0.2)
          : theme.palette.grey[300]
      }`,
      backgroundColor: colors.paper,
      boxShadow: `0 6px 12px ${alpha(theme.palette.common.black, 0.25)}`,
    },
  };
});
